import { useState } from "react";

import { Employee } from "@/Common/Api/Employee/Dto/Employee";
import { PreventiveCareCriteria } from "@/Common/Api/PreventiveCare/Dto/Responses/PreventiveCareCriteria";
import { RadioButtonv2 } from "@/Common/Components/Inputs/RadioButtonv2";
import { Modal } from "@/Common/Components/Modals/Modal";
import { useTranslation } from "@/Common/CustomHooks/useTranslation";
import { MyleHelper } from "@/Common/Helpers/MyleHelper";
import { Guid } from "@/Common/Models/Guid";
import { EmployeeUIModel } from "@/Common/Models/UI/Employee/EmployeeUIModel";
import { KeyValuePair } from "@/Common/Models/UI/KeyValuePair";

import styles from "./GenerateBonusReportPopup.module.scss";

interface IGenerateBonusReportPopupProps {
    criteriaList: PreventiveCareCriteria[];
    providers: EmployeeUIModel[];
    onClose?: () => void;
    onGenerate?: (criteriaId: Guid, providerId: Guid) => void;
}

function GenerateBonusReportPopup(props: Readonly<IGenerateBonusReportPopupProps>): JSX.Element {
    const { getText } = useTranslation();
    const currentEmployeeId = sessionStore.getCurrentEmployee().ContactFacetId;
    const defaultProviderContactFacetId = props.providers.any(p => p.ContactFacetId === currentEmployeeId) ? currentEmployeeId : null;
    const [selectedCriteriaId, setSelectedCriteriaId] = useState<Guid | null>(null);
    const [selectedProviderId, setSelectedProviderId] = useState<Guid | null>(defaultProviderContactFacetId);

    function renderFooter(): JSX.Element {
        return (
            <div className="p-footer">
                <div className="flex-container flex-end">
                    <button
                        className="btn btn-default margin-sm-left"
                        onClick={() => props.onClose?.()}
                    >
                        {getText("CancelLabel")}
                    </button>
                    <button
                        disabled={!selectedCriteriaId || !selectedProviderId}
                        className="btn btn-primary margin-sm-left"
                        onClick={() => props.onGenerate?.(selectedCriteriaId, selectedProviderId)}
                    >
                        {getText("RecallList_GenerateLabel")}
                    </button>
                </div>
            </div>
        );
    }

    return (
        <Modal
            showX
            centerContent
            draggable
            showOverlay
            title={getText("RecallList_GenerateBonusReportLabel")}
            width={520}
            maxHeight="420px"
            top={1}
            onClose={() => props.onClose?.()}
            footer={renderFooter()}
            disableAutoScroll
        >
            <p className="bold">{getText("RecallList_PreventiveActivitySelectionLabel")}</p>
            <RadioButtonv2
                htmlName="activitiesRadioControl"
                items={props.criteriaList.map(p => new KeyValuePair(p.Id, p.getTranslatedName())).toImmutable()}
                selectedItem={selectedCriteriaId}
                onChangeCallback={value => setSelectedCriteriaId(value.Key)}
            />

            <p className="bold">{getText("RecallList_ProviderSelectionLabel")}</p>
            <RadioButtonv2
                htmlName="providersRadioControl"
                items={props.providers.map(p => new KeyValuePair(p.ContactFacetId, MyleHelper.ReplaceEmptyString(Employee.getFullNameWithPrefixContactFacet(p.ContactFacetId)))).toImmutable()}
                selectedItem={selectedProviderId}
                onChangeCallback={value => setSelectedProviderId(value.Key)}
                wrapperClassName={styles.providers}
            />
        </Modal>
    );
}

export { GenerateBonusReportPopup };
